import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { MainHeader } from '../components/MainHeader';
import { Footer } from '../components/Footer';
import { SecondaryButton } from '../components/SecondaryButton';
import { PrimaryButton } from '../components/PrimaryButton';
import { Pagination } from '../components/Pagination';
import imgPlaceholder from '../../assets/messenger-lisa.png';
import {
  listAvailablePros,
  type AvailablePro,
} from '../../lib/booking-api';
import {
  patchBookingDraft,
  useBookingDraft,
} from '../../lib/booking-draft';
import { customerApiErrorMessage } from '../../lib/customer-api';
import { toast } from '../../lib/toast';
import { useCustomerAuth } from '../../lib/use-customer-auth';

const PAGE_SIZE = 12;

const ymd = (d: Date): string => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const displayName = (p: AvailablePro): string => {
  const u = p.user;
  if (!u) return 'Professional';
  const name = [u.first_name, u.last_name]
    .filter(Boolean)
    .join(' ')
    .trim();
  return name || 'Professional';
};

export default function SecondaryPreferredPage() {
  const navigate = useNavigate();
  const draft = useBookingDraft();
  const { isAuthenticated: isCustomerAuthed } = useCustomerAuth();

  const [pros, setPros] = useState<AvailablePro[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedProIds, setSelectedProIds] = useState<string[]>(() => {
    const existing = (draft?.meta?.secondary_professional_ids ?? []) as
      | string[]
      | undefined;
    return Array.isArray(existing) ? existing : [];
  });
  const [page, setPage] = useState(1);

  // Driven by the booking draft. If the customer landed here without
  // a service / date picked we can't ask the backend for availability
  // — so we show a guidance empty state instead of fake pros.
  const serviceId = draft?.service_id ?? null;
  // Memoise on the underlying ISO string — the previous version
  // depended on a Date object that was rebuilt every render, which
  // looked identical but had a fresh reference each time. That made
  // `date` change on every render and the listAvailablePros useEffect
  // re-fired on every paint (visible as the dozens of /public/
  // availability/pros calls in the network tab).
  const scheduledStartIso = draft?.scheduled_start ?? null;
  const date = useMemo(() => {
    if (scheduledStartIso) {
      const parsed = new Date(scheduledStartIso);
      if (!Number.isNaN(parsed.getTime())) return parsed;
    }
    const fallback = new Date();
    fallback.setDate(fallback.getDate() + 7);
    return fallback;
  }, [scheduledStartIso]);

  const load = async () => {
    if (!serviceId) return;
    setLoading(true);
    try {
      const res = await listAvailablePros({
        service_id: serviceId,
        date_from: ymd(date),
        date_to: ymd(date),
        duration_minutes: draft?.duration_minutes ?? undefined,
        lat: draft?.address?.latitude ?? null,
        lng: draft?.address?.longitude ?? null,
      });
      // Exclude the primary pick from the backup grid — the customer
      // already chose them in the previous step.
      const filtered = (res.data ?? []).filter(
        (p) => p.id !== draft?.professional_id,
      );
      setPros(filtered);
    } catch (err) {
      toast.error(customerApiErrorMessage(err, 'Could not load professionals.'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    serviceId,
    draft?.professional_id,
    draft?.address?.latitude,
    draft?.address?.longitude,
    scheduledStartIso,
  ]);

  const toggleProSelection = (id: string) => {
    setSelectedProIds((prev) =>
      prev.includes(id) ? prev.filter((p) => p !== id) : [...prev, id],
    );
  };

  const handleSave = () => {
    patchBookingDraft({
      meta: {
        ...(draft?.meta ?? {}),
        secondary_professional_ids: selectedProIds,
      },
    });
    // Signed-in customers proceed straight to checkout — one-off goes
    // through /your-appointment (date/time re-confirm), subscriptions
    // through /continue-booking (recurring checkout). Guests get the
    // sign-in screen with a "Create account" link.
    if (isCustomerAuthed) {
      navigate(
        draft?.mode === 'SUBSCRIPTION' ? '/continue-booking' : '/your-appointment',
      );
    } else {
      // Pass `from` so SignInForm bounces the customer back into the
      // booking flow once they're authed — otherwise they'd land on
      // /user-landing and lose the basket they just built.
      const resumeTo =
        draft?.mode === 'SUBSCRIPTION' ? '/continue-booking' : '/your-appointment';
      navigate('/signin/customer', { state: { from: resumeTo } });
    }
  };

  const totalPages = Math.max(1, Math.ceil(pros.length / PAGE_SIZE));
  const pageStart = (page - 1) * PAGE_SIZE;
  const visiblePros = pros.slice(pageStart, pageStart + PAGE_SIZE);

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <MainHeader />

      <div className="max-w-[1140px] mx-auto w-full px-4 pt-6">
        <SecondaryButton
          onClick={() => navigate(-1)}
          icon={
            <span className="material-icons text-[18px]">navigate_before</span>
          }
        >
          Back
        </SecondaryButton>
      </div>

      <main className="flex-1 max-w-[1140px] mx-auto px-4 py-8 pb-32 md:pb-12 w-full">
        <div className="text-center mb-8">
          <h1 className="font-['Playfair_Display',sans-serif] font-extrabold text-[#8e2a2f] text-[24px] leading-[36px] tracking-[-0.08px] mb-2">
            Choose secondary preferred professional
          </h1>
          <p className="font-['DM_Sans',sans-serif] text-[#908e8e] text-[14px] leading-[21px] max-w-[430px] mx-auto">
            Picking a backup helps avoid cancellations or delays to your
            service.
          </p>
        </div>

        {!serviceId ? (
          <div className="py-16 text-center max-w-[480px] mx-auto">
            <span className="material-icons text-[#eee] text-[48px] mb-3">
              info
            </span>
            <p className="font-['DM_Sans',sans-serif] font-bold text-[#3a3335] text-[16px] mb-1">
              Start your booking first
            </p>
            <p className="font-['DM_Sans',sans-serif] text-[#908e8e] text-[14px] tracking-[-0.1px]">
              We need to know which service you're booking before we can
              suggest backup pros. Head back to the home page to begin.
            </p>
          </div>
        ) : loading ? (
          <div className="py-16 text-center text-[#908e8e] font-['DM_Sans',sans-serif] text-[14px]">
            Loading available professionals…
          </div>
        ) : pros.length === 0 ? (
          <div className="py-16 text-center max-w-[480px] mx-auto">
            <span className="material-icons text-[#eee] text-[48px] mb-3">
              person_search
            </span>
            <p className="font-['DM_Sans',sans-serif] font-bold text-[#3a3335] text-[16px] mb-1">
              No backup pros available
            </p>
            <p className="font-['DM_Sans',sans-serif] text-[#908e8e] text-[14px] tracking-[-0.1px]">
              You can continue without a backup — we'll allocate someone if
              your primary pro can't make it.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-[13px] mb-5 max-w-[820px] mx-auto">
            {visiblePros.map((pro) => {
              const isSelected = selectedProIds.includes(pro.id);
              const name = displayName(pro);
              const ratingDisplay = pro.rating_avg
                ? Number(pro.rating_avg).toFixed(2)
                : '—';
              return (
                <div
                  key={pro.id}
                  onClick={() => toggleProSelection(pro.id)}
                  className={`relative bg-[#f2eee2] rounded-[6px] p-[22px] flex flex-col items-center cursor-pointer transition-all border ${
                    isSelected
                      ? 'border-[#8e2a2f] shadow-[0px_4px_15.3px_0px_rgba(134,58,58,0.1)]'
                      : 'border-[#eee] hover:border-[#8e2a2f]'
                  }`}
                >
                  <div className="absolute top-[10px] right-[10px]">
                    <div
                      className={`w-[25px] h-[25px] flex items-center justify-center ${
                        isSelected ? 'text-[#8e2a2f]' : 'text-[#eee]'
                      }`}
                    >
                      <span className="material-icons text-[24px]">
                        {isSelected ? 'check_box' : 'check_box_outline_blank'}
                      </span>
                    </div>
                  </div>

                  <div className="w-[80px] h-[80px] rounded-full overflow-hidden mb-3 mt-4">
                    <img
                      src={pro.cover_image_url ?? imgPlaceholder}
                      alt={name}
                      className="w-full h-full object-cover"
                    />
                  </div>

                  <h3 className="font-['Playfair_Display',sans-serif] font-bold text-[#3a3335] text-[18px] leading-[27px] mb-1 text-center">
                    {name}
                  </h3>

                  <div className="flex items-center gap-[5px] mb-2">
                    <span className="material-icons text-[#8e2a2f] text-[18px]">
                      star
                    </span>
                    <span className="font-['DM_Sans',sans-serif] font-bold text-[#3a3335] text-[12px] leading-[18px]">
                      {ratingDisplay}
                    </span>
                    <span className="font-['DM_Sans',sans-serif] font-bold text-[#908e8e] text-[12px] leading-[18px]">
                      ({pro.rating_count ?? 0})
                    </span>
                  </div>

                  {pro.is_featured ? (
                    <p className="font-['DM_Sans',sans-serif] text-[#8e2a2f] text-[12px] leading-[18px] tracking-[-0.1px] font-bold">
                      Featured pro
                    </p>
                  ) : (
                    <p className="font-['DM_Sans',sans-serif] text-[#908e8e] text-[12px] leading-[18px] tracking-[-0.1px]">
                      &nbsp;
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        )}

        <div className="max-w-[820px] mx-auto flex flex-col md:flex-row items-center justify-between gap-8 mt-5 mb-16">
          {/* Pagination — only visible when there's more than one page. */}
          {pros.length > PAGE_SIZE ? (
            <Pagination
              currentPage={page}
              totalPages={totalPages}
              onPageChange={(p) => setPage(p)}
              className="mt-0 w-full md:w-auto"
            />
          ) : (
            <div />
          )}

          {/* Save / Continue button — picking a backup is now optional.
              Customers who want one can tick any number of pros; those
              who don't can move straight on and we'll auto-allocate if
              the primary can't make a cycle. The button label flips
              between Save and Continue accordingly. */}
          <div className="fixed bottom-0 left-0 right-0 p-4 bg-white border-t border-[#eee] md:relative md:p-0 md:bg-transparent md:border-t-0 z-50">
            <PrimaryButton
              onClick={handleSave}
              className="px-8 h-[54px] w-full md:max-w-[400px] mx-auto block"
              disabled={!serviceId}
            >
              {selectedProIds.length > 0
                ? `Save ${selectedProIds.length} backup${selectedProIds.length === 1 ? '' : 's'}`
                : 'Continue without a backup'}
            </PrimaryButton>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
